import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { APP_COMMIT } from '../../../version';
import { CommonService } from './common.service';
import { ReloadGuardHelper } from '../../helpers/reload-guard.helper';

// A tab left open across a deploy never re-fetches index.html on its own, so
// its lazy-loaded chunks can start 404'ing (old build output) the moment the
// user navigates somewhere they haven't visited yet in this session -- e.g.
// mid-login. Polling /api/version catches that drift proactively instead of
// waiting for a chunk to actually fail.
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
// How long to wait before asking again to confirm a new commit.
const CONFIRM_DELAY_MS = 30 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AppVersionCheckService {
  private _http = inject(HttpClient);
  private _commonService = inject(CommonService);

  private _intervalId: ReturnType<typeof setInterval> | undefined;
  private _confirmTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private _pendingCommit: string | null = null;
  private _visibilityListener = (): void => {
    if (document.visibilityState === 'visible') {
      void this._checkForUpdate();
    }
  };

  public start(): void {
    if (!this._commonService.isPlatformBrowser || this._intervalId) {
      return;
    }

    this._intervalId = setInterval(
      () => void this._checkForUpdate(),
      CHECK_INTERVAL_MS
    );
    document.addEventListener('visibilitychange', this._visibilityListener);
  }

  private async _checkForUpdate(): Promise<void> {
    if (!ReloadGuardHelper.canReload()) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this._http.get<{ commit: string }>('/api/version')
      );

      const commit = response?.commit;
      if (!commit || commit === APP_COMMIT) {
        this._pendingCommit = null;
        return;
      }

      // During an ECS rolling deploy old and new tasks answer side by side, so
      // one mismatching response may come from a task that is about to go
      // away. Only reload once a later request agrees on the same new commit.
      if (this._pendingCommit === commit) {
        this._pendingCommit = null;
        ReloadGuardHelper.reloadToFreshUrl();
        return;
      }

      this._pendingCommit = commit;
      clearTimeout(this._confirmTimeoutId);
      this._confirmTimeoutId = setTimeout(
        () => void this._checkForUpdate(),
        CONFIRM_DELAY_MS
      );
    } catch (error) {
      // Network hiccups shouldn't force a reload -- only a confirmed mismatch does.
      console.warn(
        '[AppVersionCheckService] Unable to check for updates.',
        error
      );
    }
  }
}
